// ============================================================
// ByOurConsent.com -- Product Detail Page
// ============================================================

import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Nav    from '../components/Nav';
import Footer from '../components/Footer';
import useWindowWidth from '../hooks/useWindowWidth';
import api    from '../utils/api';
import { useCart } from '../context/CartContext';
import toast  from 'react-hot-toast';

// Product images can be either a local relative path (/uploads/...)
// or a full absolute URL (e.g. Printful's CDN) -- only prepend our
// backend origin for the former.
// Derives the backend origin from the same env var api.js uses, so
// production, dev, and any future domain/port all stay in sync from
// one place. Falls back to the documented dev port if unset.
const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:5002/api').replace(/\/api\/?$/, '');
const imageUrl = (path) => path ? (path.startsWith('http') ? path : `${API_ORIGIN}${path}`) : path;

// ============================================================
// Size button
// ============================================================
const ColorBtn = ({ color, colorHex, selected, onClick }) => (
    <button
        onClick={onClick}
        title={color}
        style={{
            display:      'flex',
            alignItems:   'center',
            gap:          '6px',
            background:   selected ? '#1e2a3a' : '#f5f0e8',
            color:        selected ? '#e8dfc8' : '#1e2a3a',
            border:       `0.5px solid ${selected ? '#1e2a3a' : '#c8beaa'}`,
            borderRadius: '3px',
            padding:      '6px 12px',
            fontSize:     '12px',
            fontFamily:   'sans-serif',
            cursor:       'pointer',
            transition:   'all 0.15s'
        }}>
        <span style={{
            display:      'inline-block',
            width:        '14px',
            height:       '14px',
            borderRadius: '50%',
            background:   colorHex || '#888',
            border:       '0.5px solid #c8beaa',
            flexShrink:   0
        }} />
        {color}
    </button>
);

const SizeBtn = ({ size, selected, available, modifier, onClick }) => (
    <button
        onClick={onClick}
        disabled={!available}
        style={{
            background:    selected ? '#1e2a3a' : '#f5f0e8',
            color:         selected ? '#e8dfc8' : available ? '#1e2a3a' : '#c8beaa',
            border:        `0.5px solid ${selected ? '#1e2a3a' : available ? '#c8beaa' : '#e0d8cc'}`,
            borderRadius:  '3px',
            padding:       '8px 14px',
            fontSize:      '13px',
            fontFamily:    'sans-serif',
            cursor:        available ? 'pointer' : 'not-allowed',
            transition:    'all 0.15s',
            minWidth:      '52px',
            textAlign:     'center'
        }}>
        {size}
        {modifier > 0 && (
            <span style={{ fontSize: '10px', display: 'block', opacity: 0.7 }}>
                +${modifier}
            </span>
        )}
    </button>
);

// ============================================================
// ProductPage
// ============================================================
const ProductPage = () => {
    const { slug }       = useParams();
    const navigate       = useNavigate();
    const width          = useWindowWidth();
    const isMobile       = width < 768;
    const { addItem }    = useCart();
    const [product,      setProduct]      = useState(null);
    const [loading,      setLoading]      = useState(true);
    const [selectedSize,  setSelectedSize]  = useState(null);
    const [selectedColor, setSelectedColor] = useState(null);
    const [quantity,     setQuantity]     = useState(1);
    const [added,        setAdded]        = useState(false);
    const [activeImage,  setActiveImage]  = useState(0);

    useEffect(() => {
        setLoading(true);
        api.get(`/products/${slug}`)
            .then(res => {
                setProduct(res.data.product);
                // Default to the first color, then M within that color
                // if available, otherwise the first size in that color
                const variants   = res.data.product.variants || [];
                const firstColor = variants[0]?.color || null;
                const inColor    = firstColor
                    ? variants.filter(v => v.color === firstColor)
                    : variants;
                const defaultSize = inColor.find(v => v.size === 'M') || inColor[0];
                if (firstColor)  setSelectedColor(firstColor);
                if (defaultSize) setSelectedSize(defaultSize);
            })
            .catch(() => navigate('/store', { replace: true }))
            .finally(() => setLoading(false));
    }, [slug, navigate]);

    // Unique colors this product comes in, in the order they first
    // appear among the variants
    const uniqueColors = [];
    (product?.variants || []).forEach(v => {
        if (v.color && !uniqueColors.some(c => c.color === v.color)) {
            uniqueColors.push({ color: v.color, color_hex: v.color_hex });
        }
    });

    // Only the sizes available for the currently selected color --
    // this is the actual fix: previously every color/size combination
    // rendered as one flat list of size buttons, so a product with
    // more than one color showed duplicate, indistinguishable size
    // labels (e.g. two unlabeled "S" buttons side by side).
    const sizesForSelectedColor = selectedColor
        ? (product?.variants || []).filter(v => v.color === selectedColor)
        : (product?.variants || []);

    const handleColorChange = (color) => {
        setSelectedColor(color);
        const inColor = (product?.variants || []).filter(v => v.color === color);
        const next = inColor.find(v => v.size === selectedSize?.size) || inColor.find(v => v.size === 'M') || inColor[0];
        if (next) setSelectedSize(next);
    };

    const handleAddToCart = () => {
        if (!selectedSize && product?.variants?.length > 0) {
            toast.error('Please select a size');
            return;
        }

        addItem({
            product_id:          product.id,
            variant_id:          selectedSize?.id || null,
            printful_variant_id: selectedSize?.printful_variant_id || null,
            name:                product.name,
            slug:                product.slug,
            size:                selectedSize?.size || null,
            color:               selectedSize?.color || null,
            price:               currentPrice,
            image_url:           product.primary_image_url,
            quantity,
            product_type:        product.product_type
        });

        setAdded(true);
    };

    if (loading) {
        return (
            <>
                <Nav />
                <div style={{
                    minHeight:      '60vh',
                    display:        'flex',
                    alignItems:     'center',
                    justifyContent: 'center',
                    background:     '#f5f0e8',
                    fontFamily:     'sans-serif',
                    color:          '#8a8070',
                    fontSize:       '14px'
                }}>
                    Loading product...
                </div>
                <Footer />
            </>
        );
    }

    if (!product) return null;

    const currentPrice = parseFloat(product.current_price || product.price) +
        (selectedSize ? parseFloat(selectedSize.price_modifier || 0) : 0);

    const allImages = [
        product.primary_image_url,
        ...(product.images?.map(i => i.image_url) || [])
    ].filter(Boolean);

    return (
        <>
            <Helmet>
                <title>{product.name} — By Our Consent Store</title>
                <meta name="description"
                      content={product.description || product.name} />
            </Helmet>

            <Nav />

            {/* Breadcrumb */}
            <div style={{
                background:   '#ede8dc',
                borderBottom: '0.5px solid #c8beaa',
                padding:      '8px 1.5rem',
                fontSize:     '12px',
                fontFamily:   'sans-serif',
                color:        '#8a8070'
            }}>
                <Link to="/store" style={{ color: '#8a8070', textDecoration: 'none' }}>
                    Store
                </Link>
                <span style={{ margin: '0 6px', opacity: 0.5 }}>›</span>
                <span style={{ color: '#4a5060' }}>{product.name}</span>
            </div>

            {/* Main content */}
            <div style={{
                maxWidth: '960px',
                margin:   '0 auto',
                padding:  '2rem 1.5rem',
                display:  'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap:      '3rem',
                alignItems: 'start'
            }}>

                {/* Left -- images */}
                <div>
                    {/* Main image */}
                    <div style={{
                        background:     '#ede8dc',
                        borderRadius:   '4px',
                        border:         '0.5px solid #c8beaa',
                        overflow:       'hidden',
                        height:         '420px',
                        display:        'flex',
                        alignItems:     'center',
                        justifyContent: 'center',
                        marginBottom:   '10px'
                    }}>
                        {allImages[activeImage] ? (
                            <img
                                src={imageUrl(allImages[activeImage])}
                                alt={product.name}
                                style={{
                                    width:     '100%',
                                    height:    '100%',
                                    objectFit: 'cover'
                                }}
                            />
                        ) : (
                            <div style={{
                                fontSize:   '14px',
                                fontFamily: 'sans-serif',
                                color:      '#8a8070'
                            }}>
                                By Our Consent
                            </div>
                        )}
                    </div>

                    {/* Thumbnail row */}
                    {allImages.length > 1 && (
                        <div style={{ display: 'flex', gap: '8px' }}>
                            {allImages.map((img, i) => (
                                <div
                                    key={i}
                                    onClick={() => setActiveImage(i)}
                                    style={{
                                        width:      '64px',
                                        height:     '64px',
                                        background: '#ede8dc',
                                        border:     `0.5px solid ${activeImage === i ? '#8b1a1a' : '#c8beaa'}`,
                                        borderRadius: '3px',
                                        overflow:   'hidden',
                                        cursor:     'pointer'
                                    }}>
                                    <img
                                        src={imageUrl(img)}
                                        alt={`View ${i + 1}`}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Right -- product info */}
                <div>
                    {/* Category */}
                    <div style={{
                        fontSize:      '10px',
                        fontFamily:    'sans-serif',
                        letterSpacing: '0.14em',
                        textTransform: 'uppercase',
                        color:         '#8b6a3a',
                        marginBottom:  '6px'
                    }}>
                        {product.category}
                    </div>

                    {/* Name */}
                    <h1 style={{
                        fontSize:     '28px',
                        fontWeight:   600,
                        color:        '#1e2a3a',
                        fontFamily:   "'EB Garamond', Georgia, serif",
                        lineHeight:   1.15,
                        marginBottom: '10px'
                    }}>
                        {product.name}
                    </h1>

                    {/* Price */}
                    <div style={{
                        display:      'flex',
                        alignItems:   'baseline',
                        gap:          '10px',
                        marginBottom: '1.25rem',
                        paddingBottom:'1.25rem',
                        borderBottom: '0.5px solid #c8beaa'
                    }}>
                        <span style={{
                            fontSize:   '26px',
                            fontWeight: 600,
                            color:      '#1e2a3a',
                            fontFamily: "'EB Garamond', Georgia, serif"
                        }}>
                            ${currentPrice.toFixed(2)}
                        </span>
                        {selectedSize && parseFloat(selectedSize.price_modifier) > 0 && (
                            <span style={{
                                fontSize:   '13px',
                                fontFamily: 'sans-serif',
                                color:      '#8a8070'
                            }}>
                                (includes ${parseFloat(selectedSize.price_modifier).toFixed(2)} for {selectedSize.size})
                            </span>
                        )}
                    </div>

                    {/* Color selector -- only shown when there's more
                        than one color, since a single-color product
                        doesn't need one */}
                    {uniqueColors.length > 1 && (
                        <div style={{ marginBottom: '1.25rem' }}>
                            <label style={{
                                display:       'block',
                                fontSize:      '11px',
                                fontFamily:    'sans-serif',
                                color:         '#6a7080',
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                marginBottom:  '10px'
                            }}>
                                Color
                                {selectedColor && (
                                    <span style={{ color: '#1e2a3a', marginLeft: '6px', textTransform: 'none' }}>
                                        — {selectedColor}
                                    </span>
                                )}
                            </label>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {uniqueColors.map(c => (
                                    <ColorBtn
                                        key={c.color}
                                        color={c.color}
                                        colorHex={c.color_hex}
                                        selected={selectedColor === c.color}
                                        onClick={() => handleColorChange(c.color)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Size selector -- filtered to whichever color is
                        currently selected, so a product with more than
                        one color never shows duplicate size labels */}
                    {product.variants?.length > 0 && (
                        <div style={{ marginBottom: '1.25rem' }}>
                            <div style={{
                                display:        'flex',
                                alignItems:     'center',
                                justifyContent: 'space-between',
                                marginBottom:   '10px'
                            }}>
                                <label style={{
                                    fontSize:      '11px',
                                    fontFamily:    'sans-serif',
                                    color:         '#6a7080',
                                    letterSpacing: '0.08em',
                                    textTransform: 'uppercase'
                                }}>
                                    Size
                                    {selectedSize && (
                                        <span style={{ color: '#1e2a3a', marginLeft: '6px', textTransform: 'none' }}>
                                            — {selectedSize.size}
                                        </span>
                                    )}
                                </label>
                                <a href="#size-guide" style={{
                                    fontSize:       '11px',
                                    fontFamily:     'sans-serif',
                                    color:          '#8b6a3a',
                                    textDecoration: 'none'
                                }}>
                                    Size guide
                                </a>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                {sizesForSelectedColor.map(v => (
                                    <SizeBtn
                                        key={v.id}
                                        size={v.size}
                                        selected={selectedSize?.id === v.id}
                                        available={v.stock_status === 'in_stock'}
                                        modifier={parseFloat(v.price_modifier || 0)}
                                        onClick={() => setSelectedSize(v)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Quantity */}
                    <div style={{ marginBottom: '1.25rem' }}>
                        <label style={{
                            display:       'block',
                            fontSize:      '11px',
                            fontFamily:    'sans-serif',
                            color:         '#6a7080',
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            marginBottom:  '8px'
                        }}>
                            Quantity
                        </label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0' }}>
                            <button
                                onClick={() => setQuantity(q => Math.max(1, q - 1))}
                                style={{
                                    background:   '#ede8dc',
                                    border:       '0.5px solid #c8beaa',
                                    borderRight:  'none',
                                    width:        '36px',
                                    height:       '36px',
                                    cursor:       'pointer',
                                    fontSize:     '16px',
                                    color:        '#1e2a3a',
                                    borderRadius: '3px 0 0 3px'
                                }}>
                                −
                            </button>
                            <div style={{
                                width:      '48px',
                                height:     '36px',
                                border:     '0.5px solid #c8beaa',
                                display:    'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize:   '14px',
                                fontFamily: 'sans-serif',
                                color:      '#1e2a3a',
                                background: '#f5f0e8'
                            }}>
                                {quantity}
                            </div>
                            <button
                                onClick={() => setQuantity(q => Math.min(10, q + 1))}
                                style={{
                                    background:   '#ede8dc',
                                    border:       '0.5px solid #c8beaa',
                                    borderLeft:   'none',
                                    width:        '36px',
                                    height:       '36px',
                                    cursor:       'pointer',
                                    fontSize:     '16px',
                                    color:        '#1e2a3a',
                                    borderRadius: '0 3px 3px 0'
                                }}>
                                +
                            </button>
                        </div>
                    </div>

                    {/* Add to cart / Added state */}
                    {added ? (
                        <div style={{ marginBottom: '10px' }}>
                            <div style={{
                                background:    '#3b6d11',
                                color:         '#f5f0e8',
                                padding:       '14px',
                                fontSize:      '13px',
                                fontFamily:    'sans-serif',
                                letterSpacing: '0.06em',
                                textTransform: 'uppercase',
                                borderRadius:  '3px',
                                textAlign:     'center',
                                marginBottom:  '8px'
                            }}>
                                ✓ Added to cart
                            </div>
                            <div style={{ display: 'flex', gap: '8px' }}>
                                <Link to="/cart" style={{
                                    flex:           1,
                                    background:     '#1e2a3a',
                                    color:          '#e8dfc8',
                                    padding:        '10px',
                                    fontSize:       '11px',
                                    fontFamily:     'sans-serif',
                                    letterSpacing:  '0.08em',
                                    textTransform:  'uppercase',
                                    textDecoration: 'none',
                                    borderRadius:   '3px',
                                    textAlign:      'center',
                                    display:        'block'
                                }}>
                                    View cart
                                </Link>
                                <Link to="/store" style={{
                                    flex:           1,
                                    background:     'transparent',
                                    color:          '#4a5060',
                                    padding:        '10px',
                                    fontSize:       '11px',
                                    fontFamily:     'sans-serif',
                                    letterSpacing:  '0.08em',
                                    textTransform:  'uppercase',
                                    textDecoration: 'none',
                                    borderRadius:   '3px',
                                    textAlign:      'center',
                                    border:         '0.5px solid #c8beaa',
                                    display:        'block'
                                }}>
                                    Continue shopping
                                </Link>
                            </div>
                        </div>
                    ) : (
                        <button
                            onClick={handleAddToCart}
                            style={{
                                width:         '100%',
                                background:    '#8b1a1a',
                                color:         '#f5f0e8',
                                border:        'none',
                                padding:       '14px',
                                fontSize:      '13px',
                                fontFamily:    'sans-serif',
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                cursor:        'pointer',
                                borderRadius:  '3px',
                                marginBottom:  '10px',
                                transition:    'background 0.15s'
                            }}>
                            Add to cart
                        </button>
                    )}

                    {/* Description */}
                    {product.description && (
                        <div style={{
                            paddingTop:   '1.25rem',
                            borderTop:    '0.5px solid #c8beaa',
                            marginBottom: '1rem'
                        }}>
                            <div style={{
                                fontSize:      '11px',
                                fontFamily:    'sans-serif',
                                color:         '#6a7080',
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                                marginBottom:  '8px'
                            }}>
                                About this item
                            </div>
                            <div style={{
                                fontSize:   '14px',
                                fontFamily: 'sans-serif',
                                color:      '#4a5060',
                                lineHeight: 1.6
                            }}
                                dangerouslySetInnerHTML={{
                                    __html: product.description
                                        .replace(/&amp;/g, '&')
                                        .replace(/&quot;/g, '"')
                                        .replace(/&#39;/g, "'")
                                        .replace(/&lt;/g, '<')
                                        .replace(/&gt;/g, '>')
                                }}
                            />
                        </div>
                    )}

                    {/* Shipping info */}
                    <div style={{
                        background:   '#ede8dc',
                        border:       '0.5px solid #c8beaa',
                        borderRadius: '3px',
                        padding:      '12px',
                        marginTop:    '1rem'
                    }}>
                        {[
                            { icon: '🚚', text: 'Ships in 3–7 business days via Printful' },
                            { icon: '✓',  text: 'Free shipping on orders over $50' },
                            { icon: '↩',  text: 'Defective items replaced free of charge' }
                        ].map(({ icon, text }) => (
                            <div key={text} style={{
                                display:      'flex',
                                alignItems:   'center',
                                gap:          '8px',
                                marginBottom: '6px',
                                fontSize:     '12px',
                                fontFamily:   'sans-serif',
                                color:        '#5a6070'
                            }}>
                                <span>{icon}</span>
                                <span>{text}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Size guide */}
            <div id="size-guide" style={{
                maxWidth:     '960px',
                margin:       '0 auto',
                padding:      '0 1.5rem 2rem'
            }}>
                <div style={{
                    background:   '#f5f0e8',
                    border:       '0.5px solid #c8beaa',
                    borderRadius: '4px',
                    padding:      '1.25rem'
                }}>
                    <div style={{
                        fontSize:      '12px',
                        fontWeight:    600,
                        color:         '#1e2a3a',
                        marginBottom:  '12px',
                        paddingBottom: '8px',
                        borderBottom:  '0.5px solid #c8beaa'
                    }}>
                        Size guide — Bella + Canvas 3001 (inches)
                    </div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{
                            width:          '100%',
                            borderCollapse: 'collapse',
                            fontSize:       '12px',
                            fontFamily:     'sans-serif'
                        }}>
                            <thead>
                                <tr style={{ background: '#ede8dc' }}>
                                    {['Size', 'Chest', 'Length', 'Sleeve'].map(h => (
                                        <th key={h} style={{
                                            padding:   '6px 12px',
                                            textAlign: 'left',
                                            color:     '#4a5060',
                                            fontWeight: 600,
                                            border:    '0.5px solid #c8beaa'
                                        }}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {[
                                    ['XS',  '31–33', '27',   '15.5'],
                                    ['S',   '34–37', '28',   '16.5'],
                                    ['M',   '38–41', '29',   '17.5'],
                                    ['L',   '42–45', '30',   '18.5'],
                                    ['XL',  '46–49', '31',   '19.5'],
                                    ['2XL', '50–53', '32',   '20.5'],
                                    ['3XL', '54–57', '33',   '21.5'],
                                    ['4XL', '58–61', '33.5', '22.5'],
                                    ['5XL', '62–65', '34',   '23.5'],
                                ].map(([size, chest, length, sleeve], i) => (
                                    <tr key={size}
                                        style={{ background: i % 2 === 0 ? '#f5f0e8' : '#ede8dc' }}>
                                        {[size, chest, length, sleeve].map((val, j) => (
                                            <td key={j} style={{
                                                padding: '6px 12px',
                                                color:   '#4a5060',
                                                border:  '0.5px solid #c8beaa',
                                                fontWeight: j === 0 ? 600 : 400
                                            }}>
                                                {val}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <Footer />
        </>
    );
};

export default ProductPage;
